<template>
  <!-- Kit PlayerBar (MacSounds, d44): the one bar at the foot of every
       signed-in pane while something plays (SI-3). It reads
       audio/nowPlaying.js: a session's Play all or an audition. 64 tall:
       art and a 150 px title block, the transport, the waveform filling
       the rest, then meta, Add to session (SI-25), the call to action and
       the drag handle. -->
  <div
    v-if="item"
    ref="root"
    class="player-bar"
    :class="{ 'is-narrow': narrow }"
    :style="{ '--sound': item.color || 'var(--cl-accent)' }"
  >
    <!-- Narrow panes drop the waveform; this strip along the top keeps the
         position and seeks as the waveform does. -->
    <div class="bar-progress" :class="{ seekable: canSeek }" @click="onStripClick">
      <div class="bar-progress-fill" :style="{ width: progress + '%' }" />
    </div>

    <div class="bar-left" @click="onTitleClick">
      <ArtTile :seed="item.art?.seed || item.id" :owner="item.art?.owner || item.color" :picture="item.art?.picture || null" pooled :size="40" :radius="7" />
      <div class="bar-titles">
        <div class="bar-title">{{ item.title }}</div>
        <div class="bar-sub">{{ item.subtitle }}</div>
      </div>
    </div>

    <div class="bar-transport">
      <button v-if="state.hasSteps" type="button" class="bar-step" :disabled="!state.canPrev" title="Previous sound (Up arrow)" aria-label="Previous" @click="onPrev">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h2v14H6zM20 5v14L9 12z" /></svg>
      </button>
      <button type="button" class="bar-play" :class="{ 'is-loading': state.buffering }" :title="state.playing ? 'Pause (Space)' : 'Play (Space)'" :aria-label="state.playing ? 'Pause' : 'Play'" @click="onToggle">
        <svg v-if="state.playing" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>
      </button>
      <button v-if="state.hasSteps" type="button" class="bar-step" :disabled="!state.canNext" title="Next sound (Down arrow)" aria-label="Next" @click="onNext">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 5h2v14h-2zM4 5v14l11-7z" /></svg>
      </button>
    </div>

    <div v-if="!narrow" class="bar-wave">
      <WaveMask
        :key="waveKey"
        :hash="item.waveform?.hash || ''"
        :src="item.waveform?.src || null"
        :width="1400"
        :height="72"
        :progress="progress"
        :loaded="state.loaded"
        bars
        :seekable="canSeek"
        :show-cursor="canSeek"
        @seek="onSeek"
      />
    </div>

    <div class="bar-right">
      <span v-if="item.meta" class="bar-meta">{{ item.meta }}</span>
      <button v-if="item.addToSession" type="button" class="bar-btn" @click="onAdd">Add to session</button>
      <button
        v-if="cta"
        type="button"
        class="bar-btn"
        :class="{ 'bar-btn--primary': cta.kind === 'primary' }"
        :disabled="cta.disabled"
        @click="onCta"
      >
        {{ cta.label }}
      </button>
      <!-- "⋮⋮" drawn, not set in a font: the board's 16 px glyph, a fixed
           30 x 18 cell with columns 15 apart and rows 5.5 apart. A sound
           you'd buy first keeps the same cell, empty and not draggable
           ("Buy to drag", SI-11), so the buttons don't move. -->
      <span v-if="item.dragHandle" class="bar-drag" draggable="true" title="Drag to your DAW or Finder" aria-label="Drag to your DAW or Finder" @dragstart="onDrag">
        <svg width="30" height="18" viewBox="0 0 30 18" fill="currentColor" aria-hidden="true">
          <rect v-for="(d, i) in GRIP" :key="i" :x="d[0]" :y="d[1]" width="2" height="2" rx="0.5" />
        </svg>
      </span>
      <span v-else-if="item.buyToDrag" class="bar-drag is-locked" title="Buy to drag" aria-label="Buy to drag" data-test="bar-buy-to-drag" />
    </div>
  </div>
</template>

<script>
import ArtTile from "@/components/kit/ArtTile.vue";
import WaveMask from "@/components/WaveMask.vue";
import nowPlaying from "@/audio/nowPlaying.js";
import { onDragStart } from "@/services/soundDrag.js";

// The grip's six dots (top-left corners, 2 x 2 each) in its 30 x 18 box.
const GRIP = [
  [7, 4],
  [7, 9.5],
  [7, 15],
  [22, 4],
  [22, 9.5],
  [22, 15],
];

export default {
  name: "PlayerBar",
  components: { ArtTile, WaveMask },
  emits: ["toggle", "prev", "next", "seek", "cta", "drag", "add"],
  data: () => ({ GRIP, state: nowPlaying.state, narrow: false }),
  computed: {
    item() {
      return this.state.item;
    },
    progress() {
      return this.state.progress;
    },
    canSeek() {
      return !!this.state.source;
    },
    waveKey() {
      const w = this.item?.waveform || {};
      return w.src || w.hash || this.item?.id;
    },
    // A session's "Open session" hides on that session's own page.
    cta() {
      const c = this.item?.cta;
      if (!c) return null;
      const route = this.$router.currentRoute.value;
      if (this.item.sessionName && route.name === "Session" && route.params.sessionName === this.item.sessionName) {
        return null;
      }
      return c;
    },
  },
  watch: {
    item: {
      handler() {
        this.$nextTick(this.observe);
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    // The waveform drops out when the bar has no room for it (d44).
    observe() {
      const el = this.$refs.root;
      if (!el || this.observed === el || typeof ResizeObserver === "undefined") return;
      if (this.ro) this.ro.disconnect();
      this.observed = el;
      this.ro = new ResizeObserver((entries) => {
        for (const e of entries) this.narrow = e.contentRect.width < 560;
      });
      this.ro.observe(el);
    },
    onToggle() {
      nowPlaying.toggle();
      this.$emit("toggle");
    },
    onPrev() {
      nowPlaying.prev();
      this.$emit("prev");
    },
    onNext() {
      nowPlaying.next();
      this.$emit("next");
    },
    onSeek(frac) {
      nowPlaying.seek(frac);
      this.$emit("seek", frac);
    },
    onStripClick(e) {
      if (!this.canSeek) return;
      const box = e.currentTarget.getBoundingClientRect();
      if (box.width) this.onSeek((e.clientX - box.left) / box.width);
    },
    onCta() {
      if (this.cta?.onClick) this.cta.onClick();
      this.$emit("cta");
    },
    onAdd() {
      if (typeof this.item?.addToSession === "function") this.item.addToSession();
      this.$emit("add");
    },
    onDrag(e) {
      const h = this.item?.dragHandle;
      if (h && h.sound) onDragStart(e, h.sound);
      this.$emit("drag", e);
    },
    onTitleClick() {
      if (this.item?.sessionName) {
        this.$router.push(`/session/${encodeURIComponent(this.item.sessionName)}`).catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.player-bar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  height: 64px;
  padding: 0 20px;
  background: var(--cl-dock);
  border-top: 1px solid var(--border-subtle);
  box-sizing: content-box;
}
.bar-progress {
  display: none;
}
.bar-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  cursor: pointer;
}
.bar-titles {
  width: 150px;
  min-width: 0;
}
.bar-title {
  font: 600 13px/16px var(--cl-font);
  color: var(--cl-label);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-sub {
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-transport {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.bar-step {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--cl-label-78);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.bar-step:disabled {
  opacity: 0.35;
  cursor: default;
}
.bar-play {
  position: relative;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: var(--cl-label);
  color: var(--cl-bg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.bar-play.is-loading::after {
  content: "";
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 2px solid var(--cl-accent);
  border-right-color: transparent;
  animation: bar-spin 0.9s linear infinite;
}
@keyframes bar-spin {
  to {
    transform: rotate(360deg);
  }
}
.bar-wave {
  flex: 1;
  min-width: 80px;
  height: 36px;
  --wave-bar: 1.8px;
  --wave-pitch: 3px;
}
.bar-wave :deep(.wave-mask) {
  height: 36px !important;
}
.bar-wave :deep(.wave-base) {
  background: var(--sound);
  opacity: 0.4;
}
.bar-wave :deep(.wave-loaded) {
  background: var(--sound);
  opacity: 0.55;
}
.bar-wave :deep(.wave-played) {
  background: var(--sound);
}
.bar-wave :deep(.wave-cursor) {
  width: 2px;
  margin-left: -1px;
}
.bar-right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.bar-meta {
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-label-2);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bar-btn {
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 7px;
  background: var(--cl-control-fill-strong);
  color: var(--cl-label);
  font: 600 12px/1 var(--cl-font);
  white-space: nowrap;
  cursor: pointer;
}
.bar-btn--primary {
  background: var(--cl-accent);
  color: var(--cl-on-fill);
}
.bar-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.bar-drag {
  flex: 0 0 30px;
  width: 30px;
  height: 18px;
  display: flex;
  align-items: center;
  color: var(--cl-label-2);
  cursor: grab;
}
.bar-drag.is-locked {
  cursor: default;
}
.bar-drag svg {
  display: block;
}

/* A narrow bar: the waveform goes, the strip along the top stays. */
.is-narrow .bar-progress {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--cl-control-fill-soft);
}
.is-narrow .bar-progress.seekable {
  height: 6px;
  cursor: pointer;
}
.bar-progress-fill {
  height: 100%;
  background: var(--sound);
}
</style>
